import React, { useEffect, useMemo, useState } from "react"

import { useSearchParams } from "react-router-dom"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ScreenTabsElement } from "@front-shared/elements/screenTabs.element"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../config/general.config"
import { FeatureFlagsHelper } from "../helpers/featureFlags.helper"
import { PermissionsHelper } from "../helpers/permissions.helper"
import { useAppSelector } from "../store/selectors/app.selector"
import { runtimeSelector } from "../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../store/selectors/selectedPracticeRole.selector"
import type { IScreenTab } from "../types/objects.types"

export const ScreenTabsLayoutElement = <T extends IScreenTab>(
    props: IScreenTabsLayoutElementProps<T>,
) => {
    useScrollFix()

    const [searchParams, setSearchParams] = useSearchParams()

    const runtime = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [selectedTab, setSelectedTab] = useState<T | null>(null)

    const availableTabs = useMemo<T[]>(() => {
        if (!selectedPracticeRole) {
            return []
        }

        // Filter out tabs by feature flags, permissions, and hidden tab keys
        return props.tabs.filter(item => {
            if (!runtime.enabledFeatureFlags) {
                return false
            }

            if (
                item.featureFlag &&
                !FeatureFlagsHelper.isFeatureFlagEnabled(
                    runtime.enabledFeatureFlags,
                    item.featureFlag,
                    item.featureFlagExistenceOperator,
                )
            ) {
                return false
            }

            return (
                !item.neededPermissions ||
                PermissionsHelper.hasPermission(
                    item.neededPermissions,
                    selectedPracticeRole,
                    item.neededPermissionsLogicalOperator,
                )
            )
        })
    }, [props.tabs, selectedPracticeRole?.id, runtime.enabledFeatureFlags])

    // Initialize / sync selection from available tabs. ?tab=<key> deep-links
    // once, then is cleared — subsequent runs must preserve the current tab.
    useEffect(() => {
        if (!availableTabs.length) {
            return
        }

        const tabKey = searchParams.get(GENERAL_CONFIG.urlSearchParamsKeys.tab)

        if (tabKey) {
            setSelectedTab(
                availableTabs.find(tab => tab.key === tabKey) ??
                    availableTabs[0],
            )

            searchParams.delete(GENERAL_CONFIG.urlSearchParamsKeys.tab)
            setSearchParams(searchParams)
            return
        }

        setSelectedTab(current =>
            current && availableTabs.some(tab => tab.key === current.key)
                ? current
                : availableTabs[0],
        )
    }, [searchParams, availableTabs])

    if (!selectedTab) {
        return null
    }

    return (
        <div className="relative pb-32 max-w-[840px] mx-auto">
            {availableTabs.length > 1 ? (
                <ScreenTabsElement
                    className={
                        props.tabsClassName ??
                        "w-[840px] mx-auto mt-[32px] mb-[40px] md:w-full"
                    }
                    currentTab={selectedTab}
                    tabs={availableTabs}
                    onSelected={tab => {
                        setSelectedTab(tab as T)
                    }}
                />
            ) : (
                <PrimaryText className="mt-[32px] mb-[24px]" typography="h4">
                    {selectedTab.label}
                </PrimaryText>
            )}

            {props.renderTabContent(selectedTab)}
        </div>
    )
}

interface IScreenTabsLayoutElementProps<T extends IScreenTab> {
    tabs: T[]
    renderTabContent: (tab: T) => React.ReactNode
    tabsClassName?: string
}
